import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import AnimatedLogo from '@/components/ui/AnimatedLogo';
import { Label } from '@/components/ui/label';
import { useTranslation } from 'react-i18next';
import { useToast } from '@/hooks/useToast';
import { useAuth } from '@/context/Auth';
import {
  AUTH_ROUTES,
  mapAuthError,
  normalizeAuthError,
  withTimeout,
} from '@/utils/auth';

const MIN_PASSWORD_LENGTH = 8;
const PASSWORD_UPDATE_TIMEOUT_MS = 30000;
const SIGN_OUT_TIMEOUT_MS = 8000;

const ResetPassword: React.FC = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation();
  const {
    updatePassword,
    user,
    isRecoverySession,
    clearRecoverySession,
    signOut,
  } = useAuth();

  const abandonRecoveryAndNavigate = async (route: string) => {
    clearRecoverySession();
    try {
      await withTimeout(signOut(), SIGN_OUT_TIMEOUT_MS);
    } catch {
      // Best-effort sign out; proceed with navigation regardless
    }
    navigate(route);
  };

  const hasRecoveryAccess = useMemo(
    () => Boolean(user && isRecoverySession),
    [isRecoverySession, user]
  );

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (isSubmitting) return;

    if (password.length < MIN_PASSWORD_LENGTH) {
      toast({
        title: t('reset_password.toast.error.title.translation', 'Error'),
        description: t(
          'reset_password.toast.error.too_short.translation',
          'Your new password must be at least 8 characters long.'
        ),
      });
      return;
    }

    if (password !== confirmPassword) {
      toast({
        title: t('reset_password.toast.error.title.translation', 'Error'),
        description: t(
          'reset_password.toast.error.passwords_mismatch.translation',
          'Passwords do not match.'
        ),
      });
      return;
    }

    setIsSubmitting(true);
    console.info('[auth] reset password submit started');
    try {
      const error = await withTimeout(
        updatePassword(password),
        PASSWORD_UPDATE_TIMEOUT_MS
      );

      if (error) {
        const mappedError = mapAuthError(error, 'reset_password');
        console.warn('[auth] reset password failed', {
          message: error.message,
        });
        toast({
          title: t('reset_password.toast.error.title.translation', 'Error'),
          description: t(mappedError.translationKey, mappedError.fallback),
        });
        return;
      }

      clearRecoverySession();

      try {
        await withTimeout(signOut(), SIGN_OUT_TIMEOUT_MS);
      } catch (signOutError) {
        console.warn('[auth] post-reset signout failed', {
          message: normalizeAuthError(signOutError).message,
        });
      }

      console.info('[auth] reset password success');
      toast({
        title: t('reset_password.toast.success.title.translation', 'Success'),
        description: t(
          'reset_password.toast.success.description.translation',
          'Your password has been reset. Please sign in with your new password.'
        ),
      });
      navigate(AUTH_ROUTES.login);
    } catch (error) {
      const normalizedError = normalizeAuthError(error);
      const mappedError = mapAuthError(normalizedError, 'reset_password');
      console.warn('[auth] reset password exception', {
        message: normalizedError.message,
      });
      toast({
        title: t('reset_password.toast.error.title.translation', 'Error'),
        description: t(mappedError.translationKey, mappedError.fallback),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen-dynamic flex items-center justify-center p-4 relative bg-background">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl w-full">
        <div className="flex flex-row space-x-6 justify-center items-center">
          <AnimatedLogo size={90} />
          <Label className="text-5xl md:text-8xl text-foreground">dialup</Label>
        </div>
        <div className="flex flex-col w-full items-center justify-center md:items-start p-2">
          <div className="flex flex-col justify-center md:justify-start p-10 space-y-4 w-full max-w-md">
            <h2 className="text-2xl font-bold text-foreground">
              {t('reset_password.title.translation', 'Set a new password')}
            </h2>
            {!hasRecoveryAccess ? (
              <>
                <p className="text-foreground">
                  {t(
                    'reset_password.invalid_link.translation',
                    'This reset link is invalid or has expired. Request a new one to continue.'
                  )}
                </p>
                <Button
                  type="button"
                  variant="gradient"
                  className="justify-center"
                  onClick={() =>
                    abandonRecoveryAndNavigate(AUTH_ROUTES.forgotPassword)
                  }
                >
                  {t(
                    'reset_password.request_new_link.translation',
                    'Request a new reset link'
                  )}
                </Button>
              </>
            ) : (
              <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                <Input
                  id="new-password"
                  type="password"
                  placeholder={t(
                    'reset_password.new_password.placeholder.translation',
                    'New password'
                  )}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  className="!bg-card !text-foreground !placeholder:text-muted-foreground"
                  required
                  disabled={isSubmitting}
                />
                <Input
                  id="confirm-new-password"
                  type="password"
                  placeholder={t(
                    'reset_password.confirm_password.placeholder.translation',
                    'Confirm new password'
                  )}
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  className="!bg-card !text-foreground !placeholder:text-muted-foreground"
                  required
                  disabled={isSubmitting}
                />
                <p className="text-xs text-muted-foreground">
                  {t(
                    'reset_password.password_rules.translation',
                    'Use at least 8 characters.'
                  )}
                </p>
                <Button
                  type="submit"
                  variant="gradient"
                  className="justify-center"
                  disabled={isSubmitting}
                >
                  {isSubmitting
                    ? t(
                        'reset_password.submit.loading.translation',
                        'Updating password...'
                      )
                    : t(
                        'reset_password.submit.default.translation',
                        'Update password'
                      )}
                </Button>
              </form>
            )}
            <Button
              type="button"
              variant="link"
              className="!text-foreground hover:!text-foreground/80 justify-start px-0"
              onClick={() =>
                isRecoverySession
                  ? abandonRecoveryAndNavigate(AUTH_ROUTES.login)
                  : navigate(AUTH_ROUTES.login)
              }
            >
              {t('reset_password.back_to_login.translation', 'Back to Login')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
